/**
 * objectIndexToManifest — INIT-2026-Q3-SCENE-HIERARCHY Block 3 (viewer side).
 *
 * Pure function that bridges the converter's ObjectIndex envelope to the
 * viewer's SceneHierarchyManifest without any domain/format branching.
 *
 * Design constraints:
 *   - No domain-specific logic (OBJECT_IDENTITY_AND_SELECTION_POLICY §7, DESIGN_POLICY §5).
 *   - Pure function; no side effects.
 *   - Robust to missing optional fields produced by converters that emit
 *     ObjectIndexRecord-shaped objects rather than full UniversalSceneNodes.
 *
 * Source contracts:
 *   - 30-object-indexing-contract.md §2–4 — ObjectIndex / ObjectIndexRecord
 *   - 20-two-tier-architecture.md §10     — SceneHierarchyManifest
 *   - 98-adoption-plan.md §Block3         — viewer-side object-index loading
 */

import type {
  ObjectIndex,
  ObjectIndexRecord,
  SceneHierarchyManifest,
  UniversalSceneNode,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalise one record from `ObjectIndex.objects` into a full UniversalSceneNode.
 *
 * Converters may emit either:
 *   (a) A full USN (with `id`, `aspects`, `metadata`, etc.) — used as-is with
 *       safe defaults for any absent optional fields.
 *   (b) A flat ObjectIndexRecord (no `aspects`, no `metadata`) — fields are
 *       remapped with sensible defaults so the viewer never throws.
 *
 * This dual-mode handling requires a type widening cast; the cast is local to
 * this function and never leaks beyond it.
 */
export function normalizeObjectIndexRecord(
  obj: ObjectIndexRecord,
): UniversalSceneNode {
  return normalizeNode(obj);
}

function normalizeNode(obj: ObjectIndexRecord): UniversalSceneNode {
  // Widen to access optional USN-only fields (aspects, id, parentId, metadata…)
  // that the TypeScript type does not declare on ObjectIndexRecord.
  const rec = obj as unknown as Partial<UniversalSceneNode> & ObjectIndexRecord;

  // `parentId` lives directly on USN; `parentObjectId` is the ObjectIndexRecord alias.
  const parentId: string | null =
    rec.parentId !== undefined ? rec.parentId : (rec.parentObjectId ?? null);

  const aspects: UniversalSceneNode["aspects"] =
    rec.aspects ?? { spatial: { parentId } };

  return {
    objectId: rec.objectId,
    id: rec.id ?? rec.objectId,
    parentId,
    lor: rec.lor,
    label: rec.label,
    kind: rec.kind,
    layerId: rec.layerId ?? null,
    featureId: rec.featureId ?? null,
    domain: rec.domain,
    standardRef: rec.standardRef ?? null,
    metadata: rec.metadata ?? {},
    hasChildren: rec.hasChildren ?? false,
    sortKey: rec.sortKey ?? 0,
    aspects,
    // Optional USN fields — forward when present
    ...(rec.relationships !== undefined && { relationships: rec.relationships }),
    ...(rec.designation !== undefined && { designation: rec.designation }),
    ...(rec.temporalBindings !== undefined && { temporalBindings: rec.temporalBindings }),
    ...(rec.bounds3d !== undefined && { bounds3d: rec.bounds3d }),
    ...(rec.availableSources !== undefined && { availableSources: rec.availableSources }),
    ...(rec.thumbnailUrl !== undefined && { thumbnailUrl: rec.thumbnailUrl }),
    ...(rec.childCount !== undefined && { childCount: rec.childCount }),
  };
}

/** Recompute nodeCountByLoR from a node array (used when `idx.byLoR` is absent). */
function recomputeByLoR(nodes: UniversalSceneNode[]): Record<number, number> {
  const counts: Record<number, number> = {};
  for (const node of nodes) {
    counts[node.lor] = (counts[node.lor] ?? 0) + 1;
  }
  return counts;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert a converter-produced `ObjectIndex` into a `SceneHierarchyManifest`
 * that the Scene Tree V2 can render directly.
 *
 * Field mapping:
 *   - `nodes`           ← `idx.objects` (normalised to UniversalSceneNode)
 *   - `totalNodes`      ← `idx.totalObjects ?? objects.length`
 *   - `nodeCountByLoR`  ← `idx.byLoR` (recomputed from nodes if absent/empty)
 *   - `domain`          ← domain of the first asset-kind node, else `"generic"`
 *   - `rootLayerId`     ← first node's `layerId`, else `""`
 *   - `generatedAt`     ← `new Date().toISOString()` (ingestion timestamp)
 *   - `standardName`    ← `"geosoft.unified"` (generic, domain-neutral)
 *   - `standardVersion` ← `"1"`
 *   - `paginated`       ← `false` (full index is inline)
 *   - `dataSources`     ← `{}` (populated by the viewer pipeline on demand)
 *
 * @pure No side effects; safe to call in any context.
 */
export function objectIndexToManifest(
  idx: ObjectIndex,
  opts?: { segmentBaseUrl?: string },
): SceneHierarchyManifest {
  const nodes = idx.objects.map(normalizeNode);

  // Paginated profile: the root shard carries `paginated: true`; boundary nodes
  // carry `metadata.hierarchyUrl` pointing at their child segment file. Child
  // segments are fetched + merged on expand (see SceneTreeV2Container).
  const paginated = (idx as { paginated?: boolean }).paginated === true;

  // Prefer the pre-computed byLoR from the index envelope; recompute if empty.
  const byLoRFromIndex = idx.byLoR;
  const nodeCountByLoR: Record<number, number> =
    byLoRFromIndex && Object.keys(byLoRFromIndex).length > 0
      ? byLoRFromIndex
      : recomputeByLoR(nodes);

  // Infer domain from the first asset-kind node; fall back to first node; default generic.
  const firstAsset = nodes.find((n) => n.kind === "asset") ?? nodes[0];
  const domain = firstAsset?.domain ?? "generic";

  const rootLayerId = nodes[0]?.layerId ?? "";

  return {
    schema: "geosoft.scene-hierarchy/1",
    gtwinId: idx.gtwinId,
    rootLayerId,
    generatedAt: new Date().toISOString(),
    domain,
    standardName: "geosoft.unified",
    standardVersion: "1",
    totalNodes: idx.totalObjects ?? nodes.length,
    nodeCountByLoR,
    nodes,
    paginated,
    ...(opts?.segmentBaseUrl ? { segmentBaseUrl: opts.segmentBaseUrl } : {}),
    dataSources: {},
  };
}
